export type ColumnGroup = "person" | "event" | "questions"

export type ColumnConfig = {
  id: string
  label: string
  labelShort?: string
  icon: string
  defaultWidth: number
  minWidth: number
  group: ColumnGroup
  featureFlag?: string
  isVisibleByDefault?: boolean
}

export const columnConfigs: Record<string, ColumnConfig> = {
  name: {
    id: "name",
    label: "Name",
    icon: "",
    defaultWidth: 350,
    minWidth: 280,
    group: "person",
    isVisibleByDefault: true,
  },
  email: {
    id: "email",
    label: "Email",
    icon: "emails",
    defaultWidth: 200,
    minWidth: 150,
    group: "person",
    isVisibleByDefault: true,
  },
  phone: {
    id: "phone",
    label: "Phone",
    icon: "contacts-phone",
    defaultWidth: 180,
    minWidth: 140,
    group: "person",
    isVisibleByDefault: true,
  },
  city: {
    id: "city",
    label: "City",
    icon: "event-where",
    defaultWidth: 150,
    minWidth: 120,
    group: "person",
    isVisibleByDefault: false,
  },
  country: {
    id: "country",
    label: "Country",
    icon: "event-where",
    defaultWidth: 150,
    minWidth: 120,
    group: "person",
    isVisibleByDefault: false,
  },
  companyName: {
    id: "companyName",
    label: "Company",
    icon: "settings-team1",
    defaultWidth: 180,
    minWidth: 120,
    group: "person",
    isVisibleByDefault: false,
  },
  companyTitle: {
    id: "companyTitle",
    label: "Title",
    icon: "settings-team1",
    defaultWidth: 180,
    minWidth: 120,
    group: "person",
    isVisibleByDefault: false,
  },
  emailSubscription: {
    id: "emailSubscription",
    label: "Email Subscription",
    icon: "email-only-large",
    defaultWidth: 180,
    minWidth: 140,
    group: "person",
    isVisibleByDefault: false,
  },
  whatsappOptIn: {
    id: "whatsappOptIn",
    label: "WhatsApp Opt-In",
    icon: "texts",
    defaultWidth: 160,
    minWidth: 130,
    group: "person",
    featureFlag: "whatsappMessaging",
    isVisibleByDefault: false,
  },
  tags: {
    id: "tags",
    label: "Tags",
    icon: "tag",
    defaultWidth: 180,
    minWidth: 120,
    group: "event",
    isVisibleByDefault: true,
  },
  confirmationStatus: {
    id: "confirmationStatus",
    label: "Confirmation Status",
    labelShort: "Conf. Status",
    icon: "status-all",
    defaultWidth: 220,
    minWidth: 120,
    group: "event",
    isVisibleByDefault: true,
  },
  emailValidity: {
    id: "emailValidity",
    label: "Email Validity",
    icon: "emails",
    defaultWidth: 160,
    minWidth: 120,
    group: "event",
    isVisibleByDefault: false,
  },
  emailCampaign: {
    id: "emailCampaign",
    label: "Email Campaigns",
    icon: "emails",
    defaultWidth: 200,
    minWidth: 150,
    group: "event",
    isVisibleByDefault: false,
  },
  emailsSent: {
    id: "emailsSent",
    label: "Emails Sent",
    icon: "emails",
    defaultWidth: 160,
    minWidth: 100,
    group: "event",
    isVisibleByDefault: false,
  },
  plusOnesCount: {
    id: "plusOnesCount",
    label: "Plus-One Count",
    labelShort: "+1 Count",
    icon: "settings-team1",
    defaultWidth: 150,
    minWidth: 120,
    group: "event",
    isVisibleByDefault: false,
  },
  createdTime: {
    id: "createdTime",
    label: "Created Date",
    icon: "dash-calendar",
    defaultWidth: 160,
    minWidth: 150,
    group: "event",
    featureFlag: "guestTimestamps",
    isVisibleByDefault: true,
  },
  lastModified: {
    id: "lastModified",
    label: "Last Modified",
    icon: "dash-calendar",
    defaultWidth: 160,
    minWidth: 150,
    group: "event",
    featureFlag: "guestTimestamps",
    isVisibleByDefault: false,
  },
  confirmedTime: {
    id: "confirmedTime",
    label: "Last Confirmed Date",
    labelShort: "Last Confirmed",
    icon: "dash-calendar",
    defaultWidth: 180,
    minWidth: 150,
    group: "event",
    featureFlag: "guestTimestamps",
    isVisibleByDefault: false,
  },
  checkInStatus: {
    id: "checkInStatus",
    label: "Check-In Status",
    icon: "checkin-filled",
    defaultWidth: 180,
    minWidth: 110,
    group: "event",
    isVisibleByDefault: true,
  },
  seat: {
    id: "seat",
    label: "Seating Status",
    labelShort: "Seating",
    icon: "seating",
    defaultWidth: 160,
    minWidth: 120,
    group: "event",
    isVisibleByDefault: true,
  },
}

export type ColumnId = keyof typeof columnConfigs

// ---- SORTING CONFIG ----
// Each entry maps directly to an API sort option.
// headerCycle: true means the option is part of the column header click cycle (asc → desc → clear).
// headerCycle: false means the option is only accessible via the sort menu.
export const SORT_OPTIONS = [
  {
    columnId: "name", serverOrder: "first_name_asc", direction: "asc", headerCycle: false,
  },
  {
    columnId: "name", serverOrder: "first_name_desc", direction: "desc", headerCycle: true,
  },
  {
    columnId: "name", serverOrder: "last_name_asc", direction: "asc", headerCycle: true,
  },
  {
    columnId: "name", serverOrder: "last_name_desc", direction: "desc", headerCycle: true,
  },
  {
    columnId: "email", serverOrder: "email_asc", direction: "asc", headerCycle: true,
  },
  {
    columnId: "email", serverOrder: "email_desc", direction: "desc", headerCycle: true,
  },
  {
    columnId: "companyName", serverOrder: "company_name_asc", direction: "asc", headerCycle: true,
  },
  {
    columnId: "companyName", serverOrder: "company_name_desc", direction: "desc", headerCycle: true,
  },
  {
    columnId: "companyTitle", serverOrder: "company_title_asc", direction: "asc", headerCycle: true,
  },
  {
    columnId: "companyTitle", serverOrder: "company_title_desc", direction: "desc", headerCycle: true,
  },
  {
    columnId: "confirmationStatus", serverOrder: "confirmation_status_asc", direction: "asc", headerCycle: true,
  },
  {
    columnId: "confirmationStatus", serverOrder: "confirmation_status_desc", direction: "desc", headerCycle: true,
  },
  {
    columnId: "checkInStatus", serverOrder: "check_in_status_asc", direction: "asc", headerCycle: true,
  },
  {
    columnId: "checkInStatus", serverOrder: "check_in_status_desc", direction: "desc", headerCycle: true,
  },
  {
    columnId: "seat", serverOrder: "seat_asc", direction: "asc", headerCycle: true,
  },
  {
    columnId: "seat", serverOrder: "seat_desc", direction: "desc", headerCycle: true,
  },
  {
    columnId: "createdTime", serverOrder: "created_at_asc", direction: "asc", headerCycle: true,
  },
  {
    columnId: "createdTime", serverOrder: "created_at_desc", direction: "desc", headerCycle: true,
  },
  {
    columnId: "lastModified", serverOrder: "updated_at_asc", direction: "asc", headerCycle: true,
  },
  {
    columnId: "lastModified", serverOrder: "updated_at_desc", direction: "desc", headerCycle: true,
  },
  {
    columnId: "confirmedTime", serverOrder: "confirmed_at_asc", direction: "asc", headerCycle: true,
  },
  {
    columnId: "confirmedTime", serverOrder: "confirmed_at_desc", direction: "desc", headerCycle: true,
  },
] as const

export type SortOption = typeof SORT_OPTIONS[number]

export const isColumnSortable = (columnId: ColumnId) => SORT_OPTIONS.some(
  (opt) => opt.columnId === columnId && opt.headerCycle,
)

// ---- HELPERS ----
export const getColumnWidth = (columnId: ColumnId) => columnConfigs[columnId]?.defaultWidth ?? 150

export const getColumnLabel = (columnId: ColumnId) => columnConfigs[columnId]?.labelShort
|| columnConfigs[columnId]?.label || columnId

export const getAvailableColumns = (
  featureFlags: Record<string, boolean> = {},
  group?: ColumnGroup,
) => Object.entries(columnConfigs).reduce((acc, [columnId, config]) => {
  if (config.featureFlag && !featureFlags[config.featureFlag]) {
    return acc // Skip column if feature flag is disabled
  }
  if (group && config.group !== group) {
    return acc // Skip if group filter specified and doesn't match
  }
  acc[columnId as ColumnId] = config

  return acc
}, {} as Record<ColumnId, ColumnConfig>)

export const defaultColumnsVisibility = Object.entries(
  columnConfigs,
).reduce((acc, [columnId, config]) => {
  acc[columnId] = { isVisible: config.isVisibleByDefault ?? false }

  return acc
}, {} as Record<string, { isVisible: boolean }>)
